/* eslint-disable no-undef */
import Config from '../config/index';
import Reporter from '../helpers/reporter';
import SessionCore from './session';

const JUMP_SWITCH_SESSION_KEY = 'switchConfig';
const JUMP_SWITCH_EXPRIED_TIME = 30 * 1000;

interface OnlineSwitch {
	switch: boolean;
	blackList: string[];
	timeStamp?: number;
}

export default class JumpCore {
	/**
	 * 跳转错误提示页
	 * @param reason 原因
	 * @param urlStackInfo url 堆栈信息
	 * @param envInfo 环境信息
	 * @param duration 轮询检测间隔
	 */
	static jump (reason: string, urlStackInfo: Record<string, any>[], envInfo: Record<string, any>, duration: number, errorTipsPageQuery: Record<string, any>): void {
		if (!Config.isJump || reason === 'manualCaused') {
			// 未配置跳转 || 原因为人工触发时上报警告指标
			Reporter.report(Config.REDIRECT_LOOP_WARN, urlStackInfo, envInfo, reason, Config.rules.warning.second);
			return;
		}
		this.getJumpSwitch().then((switchConfig: OnlineSwitch | null): void => {
			if (!this.checkShouldJump(switchConfig)) {
				// 线上开关关闭时上报警告指标
				Reporter.report(Config.REDIRECT_LOOP_WARN, urlStackInfo, envInfo, reason, Config.rules.warning.second);
				return;
			}
			// 清空 url 堆栈信息
			const path: string = window.location && window.location.pathname;
			SessionCore.clearStackInfo(path);
			if(this.isIE()){
				// 引导至错误提示页
				this.toLocation(`${Config.errorTipsPageUrl}?reason=${reason}`, '_self' ,errorTipsPageQuery)
				// 跳转时上报保护指标
				// 上报指标之所以放在引导至错误提示页之后，是因为如果放在前面，会在请求接口的中途因跳转页面而被迫中断，从而无法上报
				// 反之，先跳转，根据实际测试情况，在未启动跳转前就会执行请求接口(即便被中断，下一次重新进入该页面时也会重试上报，总之这样的顺序上报成功几率比前者高)
				// 主要还是因为不能对toLocation进行setTimeout，不然ie11和chrome在快速重定向时会清除任务队列，导致无法跳转错误页
				Reporter.report(Config.REDIRECT_LOOP, urlStackInfo, envInfo, reason, duration);
			}else {
				Reporter.report(Config.REDIRECT_LOOP, urlStackInfo, envInfo, reason, duration);
				setTimeout(()=>{
					this.toLocation(`${Config.errorTipsPageUrl}?reason=${reason}`, '_self' ,errorTipsPageQuery)
				}, 100)
			}

			// setTimeout(() => {
			// 	// 跳转时上报保护指标
			// 	console.log('0.1s后上报相关错误数据')

			// }, 100);
		});
	}
	/**
	 * 判断是否是ie
	 * @returns booleans
	 */
	static isIE () {
        if ('ActiveXObject' in window) { return true; }
        return false;
    }
	/**
	 * 获取跳转线上开关
	 */
	static getJumpSwitch (): Promise<OnlineSwitch | null> {
		return new Promise((resolve) => {
			// 优先从 sessionStorage 获取未过期的线上开关配置
			const storagedSwitchConfig: OnlineSwitch | null = SessionCore.getItem(JUMP_SWITCH_SESSION_KEY);
			if (this.checkSwitchConfigIsValid(storagedSwitchConfig) && this.checkSwitchConfigIsNotExpired(storagedSwitchConfig)) {
				resolve(storagedSwitchConfig);
				return;
			}
			if (typeof XMLHttpRequest === 'undefined') {
				resolve(null);
				return;
			}
			const xhr = new XMLHttpRequest();
			xhr.open('GET', 'https://portal-portm.meituan.com/safe-redirect/switch', true);
			xhr.addEventListener('readystatechange', (): void => {
				if (xhr.readyState !== 4) return;
				try {
					let response = JSON.parse(xhr.response);
					if (this.checkSwitchConfigIsValid(response)) {
						// 保存线上开关信息
						SessionCore.setItem(JUMP_SWITCH_SESSION_KEY, Object.assign(response, {
							timeStamp: Date.now()
						}));
						resolve(response);
					}
				} catch (err) {
					resolve(null);
				}
			});
			xhr.send();
		});
	}
	/**
	 * 校验是否应该跳转，校验逻辑如下：
	 * 1、线上全局开关是否关闭，关闭则不进行跳转
	 * 2、线上黑名单是否有值，若无值，表示所有项目均不在黑名单中，允许跳转
	 * 3、项目是否在黑名单中，若在则不进行跳转
	 * 4、其他情况允许跳转
	 * @param switchConfig 线上跳转开关配置
	 */
	static checkShouldJump (switchConfig: OnlineSwitch | null): boolean {
		if (!switchConfig) return false;
		if (!switchConfig.switch) return false;
		if (!switchConfig.blackList.length) return true;
		const projectName = this.getOwlProjectName();
		if (!projectName) return false;
		if (switchConfig.blackList.includes(projectName)) return false;
		return true;
	}
	/**
	 * 获取 owl 项目名称
	 */
	static getOwlProjectName (): string {
		return Config.reporter && Config.reporter.cfgManager && Config.reporter.cfgManager.config && Config.reporter.cfgManager.config.project || '';
	}
	/**
	 * 校验开关配置是否合法
	 * @param switchConfig 线上开关配置
	 */
	static checkSwitchConfigIsValid (switchConfig: OnlineSwitch | null): boolean {
		if (!switchConfig) return false;
		return typeof switchConfig.switch === 'boolean' && Array.isArray(switchConfig.blackList);
	}
	/**
	 * 校验开关配置是否未过期
	 * @param switchConfig 线上开关配置
	 */
	static checkSwitchConfigIsNotExpired (switchConfig: OnlineSwitch | null): boolean {
		if (!switchConfig || !switchConfig.timeStamp) return false;
		const now = Date.now();
		const duration = now - switchConfig.timeStamp;
		return  duration < JUMP_SWITCH_EXPRIED_TIME;
	}

	static toLocation(url: string, target?: '_blank' | '_self', query?: Record<string, any>,): void {
		const form = document.createElement('form');
		form.setAttribute('action', url);
		form.setAttribute('method', 'get');
		form.setAttribute('style', 'display:none');
		form.setAttribute('target', target || '_self');
		for(const key in query){
			const input = document.createElement('input');
			input.setAttribute('name', key);
			input.setAttribute('value', query[key]);
			form.appendChild(input)
		}
		document.body.appendChild(form);
		form.submit();
		document.body.removeChild(form);
	}
}
